import {
    RiArrowDownSLine, RiClipboardLine,
    RiFileCopyLine, RiMoonClearLine,
    RiTimeLine,
} from "@remixicon/react";
import classNames from "classnames";
import React, {
    ReactElement, ReactNode, useEffect, useState,
} from "react";
import { Form } from "react-bootstrap";
import {
    DayOfWeek, HoursOfOperations, HoursOfOperationsInput, SettingsTimeInput,
} from "../../__generated__/api";
import { hoursToTime, timeToHours } from "../../date";
import { useDebounce } from "../../debouncer";
import HolidayCountries from "../pickers/holiday-countries/HolidayCountries";
import TimePicker from "../pickers/time/TimePicker";
import TimezoneSelect from "../pickers/timezone/TimezoneSelect";
import Alert from "../theme/alert/Alert";
import Button from "../theme/button/Button";
import Dropdown from "../theme/dropdown/Dropdown";
import FormGroup from "../theme/form-group/FormGroup";
import SettingsContainer from "../theme/settings";
import Toggle from "../theme/toggle/Toggle";
import Styles from "./OfficeHours.module.scss";
import { useTheme } from "../../contexts/Theme";

interface HoursRowProps {
    dateIndex: number;
    current: HoursOfOperations;
    onChange: (dateIndex: number, start?: number, end?: number) => void;
    onReset: (dateIndex: number) => void;
    shouldUseSettings: boolean;
    copiedIndex: number | undefined;
    onCopyClick: (dateIndex: number) => void;
}

const DAY_OF_WEEK_ORDER = {
    [DayOfWeek.Monday]: 0,
    [DayOfWeek.Tuesday]: 1,
    [DayOfWeek.Wednesday]: 2,
    [DayOfWeek.Thursday]: 3,
    [DayOfWeek.Friday]: 4,
    [DayOfWeek.Saturday]: 5,
    [DayOfWeek.Sunday]: 6,
};

const DAY_OF_WEEK_INDEX = [
    DayOfWeek.Monday,
    DayOfWeek.Tuesday,
    DayOfWeek.Wednesday,
    DayOfWeek.Thursday,
    DayOfWeek.Friday,
    DayOfWeek.Saturday,
    DayOfWeek.Sunday,
];

const WEEKDAY_DAYS = [0, 1, 2, 3, 4];
const WEEKEND_DAYS = [5, 6];
const EVERY_DAY = [0, 1, 2, 3, 4, 5, 6];

const END_DAY = 23 * 60 + 59;

export const WEEKDAYS = Array.from({ length: 7 }, (__, index) => new Date(Date.UTC(2021, 0, 4 + index, 12))
    .toLocaleDateString(undefined, {
        weekday: "long",
        timeZone: "UTC",
    }));

const WEEKDAYS_SHORT = Array.from({ length: 7 }, (__, index) => new Date(Date.UTC(2021, 0, 4 + index, 12))
    .toLocaleDateString(undefined, {
        weekday: "short",
        timeZone: "UTC",
    }));

const NINE = new Date("2022-01-01T09:00:00").toLocaleTimeString(undefined, { hour: "numeric" });
const FIVE = new Date("2022-01-01T17:00:00").toLocaleTimeString(undefined, { hour: "numeric" });

const EIGHT_AM = new Date("2022-01-01T08:00:00").toLocaleTimeString(undefined, { hour: "numeric" });
const EIGHT_PM = new Date("2022-01-01T20:00:00").toLocaleTimeString(undefined, { hour: "numeric" });

function HoursRow({
    dateIndex, current, onChange, onReset, shouldUseSettings, copiedIndex, onCopyClick,
}: HoursRowProps): ReactElement {
    const isClosed = current?.startMinute === 0 && current?.endMinute === 0;
    if (shouldUseSettings) {
        return (
            <div className={classNames(Styles["hours-row"], Styles[`hours-row-${isClosed ? "closed" : "open"}`])}>
                <Toggle
                    checked={!isClosed}
                    onChange={evt => {
                        if (evt.target.checked) {
                            onReset(dateIndex);
                        } else {
                            onChange(dateIndex, 0, 0);
                        }
                    }}
                    text={WEEKDAYS_SHORT[dateIndex]}
                />
                {isClosed ? (
                    <Alert prefixIcon={<RiMoonClearLine />} variant="disabled" className={Styles["hours-closed"]}>
                        Unavailable
                    </Alert>
                ) : (
                    <>
                        <TimePicker
                            placeholder="Start Time"
                            isRequired
                            className={Styles["hours-row-time"]}
                            value={current?.startMinute as number}
                            onChange={value => onChange(dateIndex, value?.time)}
                        />
                        <div className={Styles["hours-row-seperator"]}>-</div>
                        <TimePicker
                            placeholder="Start Time"
                            isRequired
                            className={Styles["hours-row-time"]}
                            value={current?.endMinute as number}
                            onChange={value => onChange(dateIndex, undefined, value?.time)}
                        />
                    </>
                )}
                <Button
                    size="small"
                    className={classNames(Styles["icon-copy"], copiedIndex === dateIndex ? Styles["icon-copied"] : "")}
                    prefixIcon={copiedIndex != null ? <RiClipboardLine /> : <RiFileCopyLine />}
                    variant="ghost-gray"
                    onClick={() => onCopyClick(dateIndex)}
                />

            </div>
        );
    }
    return (
        <div className="d-flex align-items-center mb-1 justify-content-start">

            <Dropdown className="col-2">
                <Dropdown.Toggle variant="none" className="p-0 my-2">
                    {WEEKDAYS[dateIndex]}
                    <span className="text-gray-500 ms-3">
                        (
                        {isClosed ? "Closed" : "Open"}
                        )
                    </span>

                </Dropdown.Toggle>
                <Dropdown.Menu>
                    <Dropdown.Item onClick={() => onReset(dateIndex)}>
                        Open
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => onChange(dateIndex, 0, 0)}>
                        Closed
                    </Dropdown.Item>
                </Dropdown.Menu>
            </Dropdown>
            {!isClosed && (
                <>
                    <div className="col-2">
                        <Form.Control
                            type="time"
                            placeholder="Start Time"
                            required
                            value={hoursToTime(current?.startMinute as number)}
                            onChange={value => onChange(dateIndex, timeToHours(value.target.value))}
                        />
                    </div>
                    <div className="col-2">
                        <Form.Control
                            type="time"
                            placeholder="End Time"
                            required
                            value={hoursToTime(current?.endMinute as number)}
                            onChange={value => onChange(dateIndex, undefined, timeToHours(value.target.value))}
                        />
                    </div>
                </>
            )}

        </div>
    );
}

function Presets({
    applyPreset,
    shouldUseSettings,
}: {
    applyPreset: (days: number[], start: number, end: number) => void;
    shouldUseSettings: boolean
}) {
    return (
        <Dropdown className={shouldUseSettings ? Styles["hours-presets"] : ""}>
            <Dropdown.Toggle
                variant="none"
                className={shouldUseSettings ? "" : "p-0 my-2"}
                postfixIcon={<RiArrowDownSLine />}
            >
                Presets
            </Dropdown.Toggle>
            <Dropdown.Menu>
                <Dropdown.Item
                    onClick={() => applyPreset(EVERY_DAY, 0, END_DAY)}
                    prefixIcon={shouldUseSettings ? <RiTimeLine /> : undefined}
                >
                    24/7
                </Dropdown.Item>
                <Dropdown.Header>Weekdays</Dropdown.Header>
                <Dropdown.Item
                    onClick={() => applyPreset(WEEKDAY_DAYS, 9 * 60, 17 * 60)}
                    prefixIcon={shouldUseSettings ? <RiTimeLine /> : undefined}
                >
                    {NINE}
                    {" "}
                    to
                    {" "}
                    {FIVE}
                </Dropdown.Item>
                <Dropdown.Item
                    onClick={() => applyPreset(WEEKDAY_DAYS, 8 * 60, 20 * 60)}
                    prefixIcon={shouldUseSettings ? <RiTimeLine /> : undefined}
                >
                    {EIGHT_AM}
                    {" "}
                    to
                    {" "}
                    {EIGHT_PM}
                </Dropdown.Item>
                <Dropdown.Item
                    onClick={() => applyPreset(WEEKDAY_DAYS, 0, END_DAY)}
                    prefixIcon={shouldUseSettings ? <RiTimeLine /> : undefined}
                >
                    All Day
                </Dropdown.Item>
                <Dropdown.Item
                    onClick={() => applyPreset(WEEKDAY_DAYS, 0, 0)}
                    prefixIcon={shouldUseSettings ? <RiTimeLine /> : undefined}
                >
                    Closed
                </Dropdown.Item>
                <Dropdown.Header>Weekends</Dropdown.Header>
                <Dropdown.Item
                    onClick={() => applyPreset(WEEKEND_DAYS, 0, 0)}
                    prefixIcon={shouldUseSettings ? <RiTimeLine /> : undefined}
                >
                    Closed
                </Dropdown.Item>
                <Dropdown.Item
                    onClick={() => applyPreset(WEEKEND_DAYS, 9 * 60, 17 * 60)}
                    prefixIcon={shouldUseSettings ? <RiTimeLine /> : undefined}
                >
                    {NINE}
                    {" "}
                    to
                    {" "}
                    {FIVE}
                </Dropdown.Item>
            </Dropdown.Menu>
        </Dropdown>
    );
}
function createDefaultSchedule(): HoursOfOperationsInput[] {
    return new Array(7).fill(0).map((__, dayOfWeek) => ({
        dayOfWeek: DAY_OF_WEEK_INDEX[dayOfWeek],
    }));
}

export default function SchedulePicker({
    settings, onChange, shouldHaveBorders, shouldShowHeaders, shouldUseSettings, isLoading, isWorkflowAction = false,
}: {
    settings: SettingsTimeInput;
    onChange: (settings: SettingsTimeInput) => void;
    shouldHaveBorders?: boolean;
    shouldShowHeaders?: boolean;
    shouldUseSettings?: boolean;
    isLoading?: boolean;
    isWorkflowAction?: boolean;
}): ReactNode {
    const theme = useTheme();
    const [timezone, setTimezone] = useState<string | undefined>(
        settings.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
    );

    const [hours, setHours] = useState<HoursOfOperations[]>(createDefaultSchedule);

    const [holidayCountryCode, setHolidayCountryCode] = useState<string | undefined>(
        settings.holidayCountryCode ?? "",
    );

    const updateChange = (update: Partial<SettingsTimeInput>) => {
        onChange({
            holidayCountryCode,
            hoursOfOperations: hours,
            timezone,
            ...update,
        });
    };

    const handleChangeHours = (index: number, start?: number, end?: number) => {
        hours[index] = hours[index] ?? {
            dayOfWeek: DAY_OF_WEEK_INDEX[index],
        };
        hours[index].startMinute = start ?? hours[index].startMinute;
        hours[index].endMinute = end ?? hours[index].endMinute;

        setHours(Array.from(hours));

        updateChange({
            hoursOfOperations: hours,
        });
    };
    const handleReset = (index: number) => {
        hours[index] = {
            dayOfWeek: DAY_OF_WEEK_INDEX[index],
        };
        setHours(Array.from(hours));
        updateChange({
            hoursOfOperations: hours,
        });
    };

    const applyPreset = (days: number[], start: number, end: number) => {
        days.forEach(index => {
            hours[index] = hours[index] ?? {
                dayOfWeek: DAY_OF_WEEK_INDEX[index],
            };
            hours[index].startMinute = start ?? hours[index].startMinute;
            hours[index].endMinute = end ?? hours[index].endMinute;
        });
        setHours(Array.from(hours));
        updateChange({
            hoursOfOperations: hours,
        });
    };

    useEffect(() => {
        if (settings) {
            setTimezone(settings.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone);
            setHolidayCountryCode(settings.holidayCountryCode ?? "");
            setHours(Array.from(settings.hoursOfOperations ?? createDefaultSchedule())
                .sortBy(day => DAY_OF_WEEK_ORDER[day?.dayOfWeek as DayOfWeek])
                .map(day => ({
                    dayOfWeek: day?.dayOfWeek,
                    startMinute: day?.startMinute,
                    endMinute: day?.endMinute,
                })));
        }
    }, [settings?.timezone, settings.holidayCountryCode, JSON.stringify(settings.hoursOfOperations)]);

    const [copiedIndex, setCopiedIndex] = useState<number | undefined>(undefined);

    const resetCopied = useDebounce(() => setCopiedIndex(undefined), 3000);
    if (shouldUseSettings) {
        return (
            <SettingsContainer navigation="none">
                <SettingsContainer.Section
                    title="Time & Availability"
                    helpText="Time and availability settings allow you to manage hours of
                    operation, holidays, and the default timezone. These allow you to take
                    different actions during office hours or out-of-office hours, manage SLA
                    expectations, and set custom schedules for closed dates."
                >
                    <SettingsContainer.Setting
                        title="Timezone"
                        description="What is the default timezone? This is for things such as
                        hours of operations and SLA calculations."
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                        isLoading={isLoading}
                    >
                        <TimezoneSelect
                            className="text-gray"
                            isRequired
                            value={timezone}
                            onChange={selected => {
                                setTimezone(selected);
                                updateChange({
                                    timezone: selected,
                                });
                            }}
                        />

                    </SettingsContainer.Setting>
                    <SettingsContainer.Setting
                        title="Holiday Hours"
                        description="By specifying a holiday schedule, Foqal Agent
                            will automatically consider the company closed on the
                            standard set of holidays for that country. For example,
                            if the selected country celebrates New Years Eve,
                            and this holiday falls on a Wednesday, this day the
                            office will be considered closed regardless of
                            any other schedules."
                        isOptional
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                        isLoading={isLoading}
                    >
                        <HolidayCountries
                            value={holidayCountryCode}
                            isClearable
                            onSelected={code => {
                                setHolidayCountryCode(code);
                                updateChange({
                                    holidayCountryCode: code,
                                });
                            }}
                        />

                    </SettingsContainer.Setting>
                    <SettingsContainer.Setting
                        title="Working Schedule"
                        description="Specify your available days and hours for optimal scheduling."
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                        isLoading={isLoading}
                    >
                        <FormGroup label="Presets">
                            <Presets applyPreset={applyPreset} shouldUseSettings />
                        </FormGroup>
                        <div className={Styles["hours-list"]}>

                            {new Array(7).fill(0).map((__, index) => (
                                <HoursRow
                                    dateIndex={index}
                                    current={hours[index]}
                                    onChange={handleChangeHours}
                                    onReset={handleReset}
                                    shouldUseSettings
                                    onCopyClick={() => {
                                        if (copiedIndex != null) {
                                            const copied = hours[copiedIndex];
                                            handleChangeHours(index, copied.startMinute as number, copied.endMinute as number);
                                            resetCopied();
                                        } else {
                                            setCopiedIndex(index);
                                            resetCopied();
                                        }
                                    }}
                                    copiedIndex={copiedIndex}
                                />
                            ))}
                        </div>

                    </SettingsContainer.Setting>

                </SettingsContainer.Section>
            </SettingsContainer>
        );
    }
    return (
        <>
            <div className={shouldHaveBorders ? "border p-3 rounded mb-4 bg-white" : ""}>
                {shouldShowHeaders && (
                    <h5 className="mb-3 text-light">
                        Timezone
                    </h5>
                )}

                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">Timezone</Form.Label>
                    <TimezoneSelect
                        className="text-gray"
                        isRequired
                        value={timezone}
                        onChange={selected => {
                            setTimezone(selected);
                            updateChange({
                                timezone: selected,
                            });
                        }}
                    />
                    <Form.Text>
                        What is the default timezone? This is for things such as hours of operations and SLA calculations.
                    </Form.Text>
                </Form.Group>

            </div>
            <div className={shouldHaveBorders ? "border p-3 rounded mb-4 bg-white" : ""}>
                {shouldShowHeaders && (
                    <h5 className="mb-3 text-light">
                        Office Hours
                    </h5>
                )}

                <Form.Group className="mb-5">
                    <Form.Label className="fw-bolder">
                        Holiday Hours
                        {" "}
                        <small className="text-light">(optional)</small>
                    </Form.Label>
                    <HolidayCountries
                        value={holidayCountryCode}
                        isClearable
                        onSelected={code => {
                            setHolidayCountryCode(code);
                            updateChange({
                                holidayCountryCode: code,
                            });
                        }}
                    />
                    <Form.Text>
                        By specifying a holiday schedule, Foqal Agent will automatically consider the company closed on the
                        standard set of holidays for that country. For example, if the selected country celebrates New Years Eve,
                        and this holiday falls on a Wednesday, this day the office will be considered closed regardless of
                        any other schedules.
                    </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">Hours of Operation</Form.Label>
                    <Form.Text className="d-block mb-2">
                        You can specify what hours and days you are available.
                    </Form.Text>
                    <Presets applyPreset={applyPreset} shouldUseSettings={theme.isV3 && isWorkflowAction} />
                    <div className="p-2">
                        {Array.from({ length: 5 }, (__, index) => (
                            <HoursRow
                                dateIndex={index}
                                current={hours[index]}
                                onChange={handleChangeHours}
                                onReset={handleReset}
                                shouldUseSettings={theme.isV3 && isWorkflowAction}
                                copiedIndex={undefined}
                                onCopyClick={() => {
                                    // ignore
                                }}
                            />
                        ))}
                    </div>
                    <div className={theme.isV3 ? "p-2 rounded mt-2" : "bg-gray-200 p-2 rounded mt-2"}>
                        {Array.from({ length: 2 }, (__, index) => (
                            <HoursRow
                                dateIndex={index + 5}
                                current={hours[index + 5]}
                                onChange={handleChangeHours}
                                onReset={handleReset}
                                shouldUseSettings={theme.isV3 && isWorkflowAction}
                                copiedIndex={undefined}
                                onCopyClick={() => {
                                    // ignore
                                }}
                            />
                        ))}
                    </div>

                </Form.Group>

            </div>
        </>
    );
}

SchedulePicker.defaultProps = {
    shouldHaveBorders: true,
    shouldShowHeaders: true,
    shouldUseSettings: false,
    isLoading: false,
    isWorkflowAction: false,
};
